import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ChallengeTemplate {
  id: string;
  challenge_type: string;
  title: string;
  description: string;
  points_value: number;
  requirement_count: number;
  icon: string;
  difficulty: string;
}

export interface UserDailyChallenge {
  id: string;
  user_id: string;
  challenge_id: string;
  assigned_date: string;
  is_completed: boolean | null;
  completed_at: string | null;
  current_progress: number | null;
  points_earned: number | null;
  challenge_templates: ChallengeTemplate;
}

export interface MonthlyProgress {
  id: string;
  user_id: string;
  month: number;
  year: number;
  points_earned: number | null;
  badge_unlocked: boolean | null;
  badge_unlocked_at: string | null;
  challenges_completed: number | null;
}

const challengeService = {
  // Get user's daily challenges for today
  async getUserDailyChallenges(): Promise<UserDailyChallenge[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('user_daily_challenges')
      .select(`
        *,
        challenge_templates (*)
      `)
      .eq('assigned_date', today)
      .order('created_at');

    if (error) {
      console.error('Error fetching daily challenges:', error);
      return [];
    }

    return data || [];
  },

  // Get user's monthly progress
  async getMonthlyProgress(month?: number, year?: number): Promise<MonthlyProgress | null> {
    const now = new Date();
    const targetMonth = month || now.getMonth() + 1;
    const targetYear = year || now.getFullYear();

    const { data, error } = await supabase
      .from('user_monthly_progress')
      .select('*')
      .eq('month', targetMonth)
      .eq('year', targetYear)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching monthly progress:', error);
      return null;
    }

    return data;
  },

  // Update challenge progress
  async updateChallengeProgress(
    challengeType: string,
    incrementBy: number = 1
  ): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const today = new Date().toISOString().split('T')[0];

      // Get today's challenges of this type
      const { data: challenges } = await supabase
        .from('user_daily_challenges')
        .select(`
          *,
          challenge_templates (*)
        `)
        .eq('user_id', user.id)
        .eq('assigned_date', today)
        .eq('is_completed', false);

      if (!challenges || challenges.length === 0) return false;

      let completedAny = false;

      for (const challenge of challenges) {
        const template = challenge.challenge_templates as any;
        if (template.challenge_type !== challengeType) continue;

        const newProgress = (challenge.current_progress ?? 0) + incrementBy;
        const isCompleted = newProgress >= template.requirement_count;

        const { error: updateError } = await supabase
          .from('user_daily_challenges')
          .update({
            current_progress: newProgress,
            is_completed: isCompleted,
            completed_at: isCompleted ? new Date().toISOString() : null,
            points_earned: isCompleted ? 1 : 0
          })
          .eq('id', challenge.id);

        if (updateError) {
          console.error('Error updating challenge:', updateError);
          continue;
        }

        if (isCompleted) {
          completedAny = true;
          await this.awardChallengePoints(user.id, 1);
          toast.success(`Challenge completed! +1 badge point`, {
            description: template.title
          });
        }
      }

      return completedAny;
    } catch (error) {
      console.error('Error updating challenge progress:', error);
      return false;
    }
  },

  // Award points and check for badge unlock
  async awardChallengePoints(userId: string, points: number): Promise<void> {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();

    // Upsert monthly progress
    const { data: progress, error: progressError } = await supabase
      .from('user_monthly_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('month', month)
      .eq('year', year)
      .single();

    if (progressError && progressError.code !== 'PGRST116') {
      console.error('Error fetching progress:', progressError);
      return;
    }

    const newPoints = (progress?.points_earned || 0) + points;
    const newChallengesCompleted = (progress?.challenges_completed || 0) + 1;
    const shouldUnlockBadge = newPoints >= 50 && !progress?.badge_unlocked;

    if (progress) {
      await supabase
        .from('user_monthly_progress')
        .update({
          points_earned: newPoints,
          challenges_completed: newChallengesCompleted,
          badge_unlocked: shouldUnlockBadge || progress.badge_unlocked,
          badge_unlocked_at: shouldUnlockBadge ? new Date().toISOString() : progress.badge_unlocked_at
        })
        .eq('id', progress.id);
    } else {
      await supabase
        .from('user_monthly_progress')
        .insert({
          user_id: userId,
          month,
          year,
          points_earned: newPoints,
          challenges_completed: newChallengesCompleted,
          badge_unlocked: shouldUnlockBadge,
          badge_unlocked_at: shouldUnlockBadge ? new Date().toISOString() : null
        });
    }

    if (shouldUnlockBadge) {
      // Trigger badge unlock celebration
      window.dispatchEvent(new CustomEvent('badge-unlocked', { 
        detail: { month, year, points: newPoints } 
      }));
    }
  },

  // Get all monthly badges
  async getMonthlyBadges() {
    const { data, error } = await supabase
      .from('monthly_badges')
      .select('*')
      .order('month');

    if (error) {
      console.error('Error fetching badges:', error);
      return [];
    }

    return data || [];
  },

  // Get user's unlocked badges
  async getUserUnlockedBadges() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_monthly_progress')
      .select('month, year, badge_unlocked_at')
      .eq('user_id', user.id)
      .eq('badge_unlocked', true);

    if (error) {
      console.error('Error fetching unlocked badges:', error);
      return [];
    }

    return data || [];
  },

  // Get monthly challenge configuration
  async getMonthlyChallengeConfig(month: number, year: number) {
    const { data, error } = await supabase
      .from('monthly_challenge_assignments')
      .select('*, challenge_templates(*)')
      .eq('month', month)
      .eq('year', year)
      .order('challenge_slot');

    if (error) {
      console.error('Error fetching monthly challenge config:', error);
      return null;
    }

    return data;
  },

  // Set monthly challenge configuration
  async setMonthlyChallengeConfig(month: number, year: number, challengeIds: string[]) {
    if (challengeIds.length !== 3) {
      throw new Error('Must provide exactly 3 challenge IDs');
    }

    // Delete existing configuration for this month/year
    await supabase
      .from('monthly_challenge_assignments')
      .delete()
      .eq('month', month)
      .eq('year', year);

    // Insert new configuration
    const assignments = challengeIds.map((challengeId, index) => ({
      month,
      year,
      challenge_slot: index + 1,
      challenge_template_id: challengeId
    }));

    const { error } = await supabase
      .from('monthly_challenge_assignments')
      .insert(assignments);

    if (error) {
      console.error('Error setting monthly challenge config:', error);
      throw error;
    }
  },

  // Clear monthly challenge configuration
  async clearMonthlyChallengeConfig(month: number, year: number) {
    const { error } = await supabase
      .from('monthly_challenge_assignments')
      .delete()
      .eq('month', month)
      .eq('year', year);

    if (error) {
      console.error('Error clearing monthly challenge config:', error);
      throw error;
    }
  }
};

export default challengeService;
